import { useRef } from "react";
import { Outlet, Navigate } from "react-router-dom";
import { AppSidebar } from "./AppSidebar";
import { AppHeader } from "./AppHeader";
import { useAuth } from "@/core/AuthContext";
import { useRouteValidation } from "@/core/hooks/useRouteValidation";
import { AppScopeProvider, useAppScope } from "@/core/AppScopeContext";
import { Loader2 } from "lucide-react";
import { SidebarProvider, SidebarInset } from "@/shared/ui/sidebar";

export function AppLayout() {
  return (
    <AppScopeProvider>
      <AppLayoutInner />
    </AppScopeProvider>
  );
}

function AppLayoutInner() {
  const { user, loading, profileLoading } = useAuth();
  const { hasTenantAccess, isBootstrapping } = useAppScope();

  // Validate route when factory changes - redirects to dashboard if route is unavailable
  useRouteValidation();

  // Track whether the app has ever finished its initial boot.
  // Once booted, never unmount children for transient loading states
  // (e.g. auth token refresh on tab switch causing brief isBootstrapping flicker).
  const hasBootedRef = useRef(false);
  const isCurrentlyLoading = loading || profileLoading || isBootstrapping;

  if (!isCurrentlyLoading && user && hasTenantAccess) {
    hasBootedRef.current = true;
  }

  // Only show the full-screen spinner on initial boot, never after
  if (isCurrentlyLoading && !hasBootedRef.current) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/auth" replace />;
  }

  if (!hasTenantAccess) {
    return <Navigate to="/no-access" replace />;
  }

  // App Layout
  return (
    <SidebarProvider>
      <AppSidebar />
      <SidebarInset className="min-w-0 overflow-hidden">
        <AppHeader />
        <main className="flex-1 overflow-auto p-4 md:p-6">
          <Outlet />
        </main>
      </SidebarInset>
    </SidebarProvider>
  );
}
